import type { AuthMeResponse } from "@pulsar-anvil-studios/api-types";
import { AUTH_GET_ME } from "@pulsar-anvil-studios/routes/auth";
import ky from "ky";
import { api, apiPath, refreshSession } from "@/api/client";

const REFRESH_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes

/** Module-level timestamp — immune to React render-cycle delays. */
let lastRefreshedTimestamp: number | null = null;

/** Mark the session as freshly refreshed (call after login or successful refresh). */
export const markSessionRefreshed = (): void => {
  lastRefreshedTimestamp = Date.now();
};

/** Reset to null for testing (simulates fresh session). */
export const resetLastRefreshed = (): void => {
  lastRefreshedTimestamp = null;
};

/** Expire the debounce window for testing (keeps "not first call" semantics). */
export const expireLastRefreshed = (): void => {
  lastRefreshedTimestamp = Date.now() - REFRESH_INTERVAL_MS - 1;
};

/** Fetch the current user identity from GET /auth/me. Uses the api client (has 401 retry). */
export const fetchIdentity = async (): Promise<AuthMeResponse> =>
  api.get(apiPath(AUTH_GET_ME)).json<AuthMeResponse>();

/**
 * Refresh the session and conditionally fetch the updated identity.
 *
 * 1. POST /auth/refresh — rotates HttpOnly cookies, returns `{ permissionsChanged }`
 * 2. GET /auth/me — only when `permissionsChanged` is true or on first call
 *
 * Skips the refresh call if the last refresh was recent (< 4 min)
 * to avoid WAF rate limits. Uses a module-level timestamp so the
 * debounce works even when the router context is stale.
 *
 * Uses raw ky for /auth/me (not the api client) to avoid triggering
 * nested refresh via the 401 retry hook.
 *
 * Returns `true` on success, `false` on failure (sets identity to null).
 */
export const tryRefreshToken = async (
  setIdentity: (id: AuthMeResponse | null) => void,
): Promise<boolean> => {
  if (lastRefreshedTimestamp && Date.now() - lastRefreshedTimestamp < REFRESH_INTERVAL_MS) {
    return true;
  }

  try {
    const { permissionsChanged } = await refreshSession();
    const firstCall = !lastRefreshedTimestamp;
    lastRefreshedTimestamp = Date.now();

    if (permissionsChanged || firstCall) {
      const identity = await ky
        .get(apiPath(AUTH_GET_ME), {
          prefix: import.meta.env.VITE_API_URL,
          credentials: "include",
          retry: 0,
        })
        .json<AuthMeResponse>();
      setIdentity(identity);
    }

    return true;
  } catch {
    setIdentity(null);
    return false;
  }
};
